import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Input, Button, Modal } from 'antd';
import type { InputRef } from 'antd/es/input';
import { useSelector, useStore } from 'react-redux';
import logo from "../assets/images/logo.png";
import logoDark from "../assets/images/logo-dark.png";
import { useAppDispatch } from "../store/index";
import type { RootState } from "../store/index";
import { dismissSignOutNotice, signOutReasonSelector } from "../store/user/slice";
import { ExclamationCircleFilled } from '@ant-design/icons';
import { AuthStore, signIn } from '../auth/auth-controller';
import {
    AuthApiError,
    GENERIC_CONNECTION_MESSAGE,
    GENERIC_CREDENTIAL_MESSAGE,
    GENERIC_FORBIDDEN_MESSAGE,
    GENERIC_SERVER_MESSAGE,
    toAuthApiError,
} from '../auth/auth-errors';

/**
 * Used only when a 429 arrives with neither a readable `Retry-After`
 * header nor a `retry_after_seconds` body value. Conservative on purpose:
 * long enough that a client cannot hammer the Backend, short enough that
 * a user is not locked out by a parsing problem. The Backend's real value
 * is always preferred when present.
 */
export const RETRY_AFTER_FALLBACK_SECONDS = 60;

/**
 * Shown when the user was signed out WITHOUT asking to be -- the Backend
 * refused the bearer token with 401, either on startup or on a request
 * made while they were working.
 *
 * It names both real causes because the Backend answers 401 identically
 * for a revoked, a replaced and an expired session
 * (`common/auth.py::require_actor` raises one generic message on purpose,
 * so an unauthenticated caller cannot probe which). Claiming "another
 * device signed in" outright would be the Website inventing a fact it was
 * never told; naming the likely cause first and the alternative second is
 * both useful and true.
 */
export const SESSION_ENDED_MESSAGE = 'มีการเข้าสู่ระบบบัญชีนี้จากอุปกรณ์อื่น หรือเซสชันหมดอายุแล้ว ระบบจึงออกจากระบบให้อัตโนมัติ กรุณาเข้าสู่ระบบอีกครั้ง';

/**
 * Shown when the Backend said an administrator revoked this device.
 *
 * Deliberately different from `SESSION_ENDED_MESSAGE`, which tells the user
 * their account was reached from another device. For a kicked employee that
 * is false, and the reasonable reaction to it -- assume a break-in, change
 * the password, raise an alarm -- is all wasted. This names what happened
 * and what to do, without blaming the employee for anything.
 */
export const REVOKED_BY_ADMIN_MESSAGE = 'ผู้ดูแลระบบนำอุปกรณ์นี้ออกจากระบบ กรุณาเข้าสู่ระบบอีกครั้ง หากไม่แน่ใจว่าเพราะอะไร กรุณาสอบถามผู้ดูแลระบบ';

/** The notice for [reason], or '' when there is nothing to say. */
export const signOutNotice = (reason: string | undefined | null): string => {
    if (reason === 'revokedByAdmin') return REVOKED_BY_ADMIN_MESSAGE;
    if (reason === 'sessionEnded') return SESSION_ENDED_MESSAGE;
    return '';
};

const clientTypeLabel = (clientType: string | null): string => {
    if (clientType === 'web') return 'เว็บ';
    if (clientType === 'mobile') return 'มือถือ';
    return clientType || '';
};

/**
 * Phase 7C-4 -- the login page now speaks the Phase 7C-2 session
 * contract (`SESSION_CREATE`) instead of the legacy employee LOGIN.
 *
 * What changed, and why:
 *   - no `localStorage` writes: the token goes to `sessionStorage` via
 *     the auth controller, the actor lives in Redux, and nothing else is
 *     stored (the always-true "remember" flag is gone with it);
 *   - the demo/bypass button is gone: it wrote a fake admin into
 *     `localStorage.uuid` and navigated straight in, which the gate in
 *     App.tsx would refuse anyway;
 *   - errors are branched on HTTP status: 401 = one generic message for
 *     both unknown user and wrong password; 409 = ask before replacing;
 *     429 = honour the Backend's `Retry-After` with a live countdown.
 *
 * Routing after a successful login is done by the gate in App.tsx
 * (`signedIn` on `/login` -> `default_page`), so this component has a
 * single job.
 */
const Login: React.FC = (prop) => {

    const dispatch = useAppDispatch();
    const reduxStore = useStore<RootState>();
    const authStore: AuthStore = useMemo(
        () => ({ dispatch, getState: reduxStore.getState }),
        [dispatch, reduxStore]
    );
    const signOutReason = useSelector(signOutReasonSelector);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    // 429 cool-down: the moment (ms epoch) the Backend allows a retry, or
    // null. Kept as a timestamp rather than a decrementing counter so a
    // re-render can never restart or duplicate the countdown.
    const [retryAt, setRetryAt] = useState<number | null>(null);
    const [remaining, setRemaining] = useState(0);
    const passRef = useRef<InputRef>(null);
    const btnRef = useRef<HTMLInputElement>(null);

    const onUsernameChange = (event: any) => {
        setUsername(event.target.value);
    }

    const onPasswordChange = (event: any) => {
        setPassword(event.target.value);
    }

    const getConfigModal = (titile: string, content: React.ReactNode) => {
        return {
            title: titile,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: content,
            okText: 'ตกลง',
            cancelText: 'ยกเลิก',
            centered: true
        }
    }

    // One interval per cool-down, created when `retryAt` is set and torn
    // down when it clears or the component unmounts.
    useEffect(() => {
        if (retryAt === null) return;
        const tick = () => {
            const left = Math.ceil((retryAt - Date.now()) / 1000);
            if (left <= 0) {
                setRetryAt(null);
                setRemaining(0);
            } else {
                setRemaining(left);
            }
        };
        tick();
        const timer = window.setInterval(tick, 1000);
        return () => window.clearInterval(timer);
    }, [retryAt]);

    const startCooldown = (seconds: number) => {
        const whole = Math.max(1, Math.ceil(seconds));
        setRemaining(whole);
        setRetryAt(Date.now() + whole * 1000);
    };

    const confirmReplaceSession = (error: AuthApiError) => {
        const active = error.activeSession;
        const deviceText = active
            ? `${active.device_label || 'ไม่ระบุชื่ออุปกรณ์'}${active.client_type ? ` (${clientTypeLabel(active.client_type)})` : ''}`
            : null;
        Modal.confirm({
            ...getConfigModal('มีการเข้าสู่ระบบบนอุปกรณ์อื่น', (
                <div className='login-conflict'>
                    <p>บัญชีนี้กำลังใช้งานอยู่บนอุปกรณ์อื่น ต้องการออกจากอุปกรณ์เดิมและเข้าสู่ระบบบนเครื่องนี้หรือไม่</p>
                    { deviceText
                        ? <div className='login-conflict-meta' data-testid='login-conflict-device'>อุปกรณ์: {deviceText}</div>
                        : null }
                </div>
            )),
            okText: 'เข้าสู่ระบบบนเครื่องนี้',
            cancelText: 'ยกเลิก',
            // Nothing is replaced unless the user presses this button. The
            // request re-sends the credentials the user typed, plus a real
            // boolean `replace_existing: true`.
            onOk: () => submit(true),
        });
    };

    const showError = (title: string, content: string) => {
        Modal.error({
            ...getConfigModal(title, content),
        });
    };

    const submit = async (replaceExisting: boolean) => {
        if (submitting || retryAt !== null) return;

        const code = username.trim();
        if (!code || !password) {
            showError('ผิดพลาด', 'กรุณากรอกรหัสผู้ใช้และรหัสผ่าน');
            return;
        }

        setSubmitting(true);
        try {
            await signIn(authStore, { code, password, replaceExisting });
            // Success: App.tsx sees `signedIn` and routes to `default_page`.
        } catch (raw) {
            const error = toAuthApiError(raw);
            if (error.isSessionConflict) {
                confirmReplaceSession(error);
            } else if (error.isRateLimited) {
                startCooldown(error.retryAfterSeconds ?? RETRY_AFTER_FALLBACK_SECONDS);
            } else if (error.isUnauthenticated) {
                // Same text for an unknown code and a wrong password, so the
                // login page cannot be used to list valid employee codes.
                showError('ผิดพลาด', GENERIC_CREDENTIAL_MESSAGE);
            } else if (error.isForbidden) {
                showError('ไม่มีสิทธิ์', GENERIC_FORBIDDEN_MESSAGE);
            } else if (error.isNetwork) {
                showError('ผิดพลาด', GENERIC_CONNECTION_MESSAGE);
            } else if (error.isServerError) {
                showError('ผิดพลาด', GENERIC_SERVER_MESSAGE);
            } else {
                showError('ผิดพลาด', error.message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const onLogin = () => {
        submit(false);
    };

    const nextFocus = (event: any, key: number) => {
        if (event.keyCode === 13) {
            if (key === 0) {
                passRef.current?.focus();
            } else if (key === 1) {
                btnRef.current?.click();
            }
        }
    }

    const cooling = retryAt !== null;
    const buttonLabel = cooling
        ? `ลองใหม่ในอีก ${remaining} วินาที`
        : (submitting ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ 🚀');

    return (
        <>
            <div className='loginContainer'>
                <div className='logoBox'>
                    <div className='loginInput'>
                        <div className='login-title-box'>
                            <FontAwesomeIcon icon="user" className='userLoginIcon' />
                            <h2>ยินดีต้อนรับ ✨</h2>
                            <p>กรุณาลงชื่อเข้าใช้งานระบบ JinDa POS</p>
                        </div>
                        { signOutNotice(signOutReason)
                            ? <div className='login-session-notice' role='alert' data-testid='login-session-notice'>
                                <span className='login-session-notice-icon' aria-hidden='true'>⚠️</span>
                                <span className='login-session-notice-text'>{signOutNotice(signOutReason)}</span>
                                <button
                                    type='button'
                                    className='login-session-notice-close'
                                    onClick={() => dispatch(dismissSignOutNotice())}
                                    aria-label='ปิดข้อความแจ้งเตือน'
                                    data-testid='btn-dismiss-session-notice'
                                >
                                    ×
                                </button>
                              </div>
                            : null }
                        <div className='logoBoxInput'>
                            <label>รหัสผู้ใช้</label>
                            <Input className='uppercase' value={username} placeholder={'กรุณากรอกรหัสผู้ใช้'} onChange={onUsernameChange} onKeyUp={(e) => { nextFocus(e, 0) }} style={{textTransform: 'uppercase'}} disabled={submitting} autoComplete='username' />
                        </div>
                        <div className='logoBoxInput'>
                            <label>รหัสผ่าน</label>
                            <Input.Password ref={passRef} value={password} placeholder={'กรุณากรอกรหัสผ่าน'} onChange={onPasswordChange} onKeyUp={(e) => { nextFocus(e, 1) }} disabled={submitting} autoComplete='current-password' />
                        </div>
                        <div style={{marginTop: "24px", textAlign: "center"}}>
                            <Button className='btn-common' ref={btnRef} type="primary" onClick={onLogin} loading={submitting} disabled={submitting || cooling} data-testid='btn-login' style={{width: '100%', height: '44px', fontSize: '15px'}}>
                                {buttonLabel}
                            </Button>
                            { cooling
                                ? <div className='login-cooldown-hint' role='status' aria-live='polite' data-testid='login-cooldown'>
                                    มีการพยายามเข้าสู่ระบบมากเกินไป กรุณารอ {remaining} วินาที ก่อนลองใหม่อีกครั้ง
                                  </div>
                                : null }
                        </div>
                    </div>
                    <div className='loginLogo'>
                        {/* Both marks ship and CSS picks one, the same way
                            nav.tsx swaps them -- but without needing the
                            theme in React state. The login page has no
                            theme toggle of its own, yet it inherits
                            `data-theme` from whatever the user chose
                            before signing out, and the default mark's
                            wordmark is dark ink that disappears on the
                            dark card. */}
                        <div className='logo-wrapper'>
                            <img src={logo} alt="JinDa POS Logo" className='brand-logo-light' />
                            <img src={logoDark} alt="" aria-hidden="true" className='brand-logo-dark' />
                        </div>
                        <div className='brand-tagline'>JinDa Point of Sale 🥕</div>
                        <div className='brand-desc'>ระบบบริหารจัดการร้านค้า จุดขาย และคลังสินค้า</div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Login;
